import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import io from 'socket.io-client';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const SocketContext = createContext();

export const useSocket = () => {
    return useContext(SocketContext);
};

// ─────────────────────────────────────────────────────────────────────────────
// MODULE-LEVEL AUDIO ENGINE
// Placed OUTSIDE React so it survives component remounts.
// Uses HTMLAudioElement + WAV blob — far more reliable than AudioContext in prod.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a minimal PCM WAV blob for a given frequency/duration.
 * All math runs synchronously at module load — no async, no AudioContext needed.
 */
function buildWavBlob(freqs, duration = 0.18, sampleRate = 22050) {
    const numSamples = Math.floor(sampleRate * duration);
    const buf = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buf);

    const ws = (off, str) => {
        for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i));
    };
    ws(0, 'RIFF'); view.setUint32(4, 36 + numSamples * 2, true);
    ws(8, 'WAVE'); ws(12, 'fmt ');
    view.setUint32(16, 16, true);   // chunk size
    view.setUint16(20, 1, true);    // PCM
    view.setUint16(22, 1, true);    // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);    // block align
    view.setUint16(34, 16, true);   // 16-bit
    ws(36, 'data'); view.setUint32(40, numSamples * 2, true);

    for (let i = 0; i < numSamples; i++) {
        const t = i / sampleRate;
        const env = Math.max(0, 1 - t / duration); // linear decay
        let s = 0;
        for (const f of freqs) s += Math.sin(2 * Math.PI * f * t);
        s = (s / freqs.length) * env * 0.75;
        view.setInt16(44 + i * 2, Math.max(-32767, Math.min(32767, Math.round(s * 32767))), true);
    }
    return new Blob([buf], { type: 'audio/wav' });
}

// Pre-build audio clips for each sound type (runs once at import time)
let _audioClips = null;
let _audioUnlocked = false;

function getClips() {
    if (_audioClips) return _audioClips;
    try {
        const makeAudio = (blob) => {
            const a = new Audio(URL.createObjectURL(blob));
            a.preload = 'auto';
            return a;
        };
        _audioClips = {
            'double-beep':    [makeAudio(buildWavBlob([880, 440], 0.14)), makeAudio(buildWavBlob([880, 440], 0.14))],
            'slow-pulse':     [makeAudio(buildWavBlob([330], 0.4))],
            'high-siren':     [makeAudio(buildWavBlob([988], 0.14)), makeAudio(buildWavBlob([1318], 0.14))],
            'digital-chime':  [makeAudio(buildWavBlob([523.25], 0.14)), makeAudio(buildWavBlob([659.25], 0.14)), makeAudio(buildWavBlob([783.99], 0.14))],
        };
    } catch (e) {
        console.warn('Audio clip init failed:', e);
        _audioClips = {};
    }
    return _audioClips;
}

/**
 * Play audio clip at index for the given sound type.
 * Returns a promise that resolves when done (or immediately on failure).
 */
async function playClip(soundType, index = 0) {
    const clips = getClips();
    const list = clips[soundType];
    if (!list || !list.length) return;
    const clip = list[index % list.length];
    if (!clip) return;
    try {
        clip.currentTime = 0;
        await clip.play();
    } catch (e) {
        // Autoplay still blocked; _audioUnlocked flag will prevent future attempts
        _audioUnlocked = false;
    }
}

/**
 * Called on first user gesture (module level, survives React remounts).
 * Silently plays+pauses each clip to "unlock" the HTMLAudioElement.
 */
async function unlockAudio() {
    if (_audioUnlocked) return;
    const clips = getClips();
    const all = Object.values(clips).flat();
    let ok = true;
    for (const clip of all) {
        try {
            clip.volume = 0;
            await clip.play();
            clip.pause();
            clip.currentTime = 0;
            clip.volume = 1;
        } catch (_) { ok = false; }
    }
    _audioUnlocked = ok;
}

// Register unlock listeners at MODULE LEVEL — capture phase so they fire before anything else
if (typeof window !== 'undefined') {
    ['pointerdown', 'click', 'keydown', 'touchstart'].forEach((evt) => {
        window.addEventListener(evt, unlockAudio, { capture: true, passive: true });
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE-LEVEL CSS INJECTION
// Inject @keyframes once so the portal modal doesn't need Emotion / MUI.
// ─────────────────────────────────────────────────────────────────────────────

if (typeof document !== 'undefined') {
    const s = document.createElement('style');
    s.id = 'nilab-alert-styles';
    s.textContent = `
        @keyframes nilab-overlay-in { from { opacity:0 } to { opacity:1 } }
        @keyframes nilab-modal-in   { from { opacity:0; transform:translate(-50%,-60%) scale(.9) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }
        @keyframes nilab-flash      { 0%,100% { border-color:rgba(211,47,47,.35); background:rgba(211,47,47,.04) } 50% { border-color:rgba(211,47,47,.9); background:rgba(211,47,47,.12) } }
        @keyframes nilab-pulse      { 0%,100% { transform:scale(1); box-shadow:0 0 0 rgba(211,47,47,.6) } 50% { transform:scale(1.18); box-shadow:0 0 18px rgba(211,47,47,.8) } }
        @keyframes nilab-badge      { 0%,100% { transform:scale(1) } 40% { transform:scale(1.15) } }
    `;
    if (!document.getElementById('nilab-alert-styles')) {
        document.head.appendChild(s);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// NEW ORDER ALERT MODAL  — plain React + ReactDOM.createPortal, zero MUI usage
// ─────────────────────────────────────────────────────────────────────────────

const NewOrderAlertModal = ({ alert, onClose }) => {
    const navigate = useNavigate();

    const handleView = () => {
        navigate(alert.orderId ? `/vendor/orders/${alert.orderId}` : '/vendor/orders');
        onClose();
    };

    const modal = (
        <>
            {/* Backdrop */}
            <div
                role="button"
                tabIndex={0}
                onClick={onClose}
                onKeyDown={(e) => e.key === 'Escape' && onClose()}
                style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    zIndex: 999998,
                    animation: 'nilab-overlay-in .2s ease forwards'
                }}
            />

            {/* Card */}
            <div style={{
                position: 'fixed',
                top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                zIndex: 999999,
                width: 'min(480px, calc(100vw - 32px))',
                background: 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(12px)',
                borderRadius: '18px',
                border: '2px solid rgba(211,47,47,.5)',
                boxShadow: '0 12px 48px rgba(0,0,0,.22)',
                padding: '36px 28px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
                textAlign: 'center',
                animation: 'nilab-modal-in .3s cubic-bezier(.34,1.56,.64,1) forwards, nilab-flash 1.5s infinite ease-in-out'
            }}>
                {/* Bell icon */}
                <div style={{
                    width: 80, height: 80,
                    borderRadius: '50%',
                    background: '#ffebee',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 40,
                    animation: 'nilab-pulse 1.4s infinite ease-in-out'
                }}>
                    🔔
                </div>

                {/* Badge */}
                <div style={{
                    background: '#d32f2f', color: '#fff',
                    borderRadius: '999px', padding: '4px 16px',
                    fontSize: 12, fontWeight: 700, letterSpacing: 1.2,
                    animation: 'nilab-badge 1.4s infinite ease-in-out'
                }}>
                    URGENT
                </div>

                <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#b71c1c', marginBottom: 8 }}>
                        New Order Placed!
                    </div>
                    <div style={{ fontSize: 15, color: '#555', lineHeight: 1.6 }}>
                        Order <strong style={{ color: '#d32f2f' }}>{alert.orderCode || 'Incoming'}</strong> is waiting.
                        Please accept or start preparing immediately.
                    </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 12, width: '100%', justifyContent: 'center' }}>
                    <button
                        onClick={onClose}
                        style={{
                            flex: 1, maxWidth: 160,
                            padding: '12px 0', borderRadius: 10,
                            border: '1.5px solid #ccc', background: '#fff',
                            color: '#555', fontSize: 15, fontWeight: 600,
                            cursor: 'pointer', transition: 'all .2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
                        onFocus={e => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseOut={e => e.currentTarget.style.background = '#fff'}
                        onBlur={e => e.currentTarget.style.background = '#fff'}
                    >
                        Dismiss
                    </button>
                    <button
                        onClick={handleView}
                        style={{
                            flex: 1, maxWidth: 160,
                            padding: '12px 0', borderRadius: 10,
                            border: 'none', background: '#d32f2f',
                            color: '#fff', fontSize: 15, fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(211,47,47,.4)',
                            transition: 'all .2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#b71c1c'}
                        onFocus={e => e.currentTarget.style.background = '#b71c1c'}
                        onMouseOut={e => e.currentTarget.style.background = '#d32f2f'}
                        onBlur={e => e.currentTarget.style.background = '#d32f2f'}
                    >
                        View Order
                    </button>
                </div>
            </div>
        </>
    );

    return ReactDOM.createPortal(modal, document.body);
};

// ─────────────────────────────────────────────────────────────────────────────
// SOCKET PROVIDER
// ─────────────────────────────────────────────────────────────────────────────

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [newOrderAlert, setNewOrderAlert] = useState(null);
    const customization = useSelector((state) => state.customization);
    const userInfo = customization?.userInfo;

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:7000';

    // ── ALARM LOOP ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!newOrderAlert) return;

        const soundType = localStorage.getItem('vendorOrderAlertSound') || 'double-beep';
        if (soundType === 'mute') return;

        const clips = getClips()[soundType] || [];
        if (!clips.length) return;

        let clipIndex = 0;
        let active = true;

        const tick = async () => {
            if (!active) return;
            await playClip(soundType, clipIndex);
            clipIndex = (clipIndex + 1) % clips.length;

            if (!active) return;

            // For double-beep: play second beep 200ms after the first, then pause 600ms
            // For others: repeat on the sound's natural duration + a short gap
            const gap = soundType === 'double-beep' && clipIndex === 1 ? 200
                : soundType === 'slow-pulse' ? 800
                : 300;

            setTimeout(tick, gap);
        };

        tick();

        return () => { active = false; };
    }, [newOrderAlert]);

    // ── FETCH INITIAL NOTIFICATIONS ──────────────────────────────────────────
    const fetchNotifications = useCallback(async () => {
        if (!userInfo?.accountType) return;
        try {
            const role = userInfo.accountType === 'admin' ? 'admin' : 'vendor';
            const { default: NotificationService } = await import('api/NotificationService');
            const response = await NotificationService.getNotifications(role, { page: 1, limit: 10 });
            const data = response.data || response;
            setNotifications(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, [userInfo]);

    // ── SOCKET CONNECTION ────────────────────────────────────────────────────
    useEffect(() => {
        if (!userInfo?.id) return;

        fetchNotifications();

        const newSocket = io(SOCKET_URL, { query: { userId: userInfo.id } });

        newSocket.on('connect', () => {
            console.log('Socket connected:', newSocket.id);
            setIsConnected(true);
            newSocket.emit('join', userInfo.id);
            if (userInfo?.accountType === 'admin') {
                newSocket.emit('join_admin', userInfo.id);
            } else if (userInfo?.accountType === 'vendor') {
                newSocket.emit('join_vendor', userInfo.vendorId || userInfo.id);
            }
        });

        newSocket.on('disconnect', () => { console.log('Socket disconnected'); setIsConnected(false); });
        newSocket.on('connect_error', (err) => { console.error('Socket error:', err); setIsConnected(false); });

        const handleNewNotification = (notification) => {
            console.log('Notification received:', notification);
            setNotifications((prev) => [notification, ...prev]);

            const isNewOrder =
                notification.title === 'New Order' ||
                notification.orderId ||
                (notification.message && notification.message.toLowerCase().includes('new order'));

            if (userInfo?.accountType === 'vendor' && isNewOrder) {
                setNewOrderAlert({
                    orderId:   notification.orderId   || '',
                    orderCode: notification.orderCode || 'New',
                    message:   notification.message   || 'You have a new order.'
                });
            }

            // toast
            import('react-toastify').then(({ toast }) => {
                toast.info(
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{notification.title}</div>
                        <div style={{ fontSize: '0.85rem' }}>{notification.message}</div>
                    </div>,
                    { position: 'top-right', autoClose: 5000, hideProgressBar: false,
                      closeOnClick: true, pauseOnHover: true, draggable: true }
                );
            });
        };

        if (userInfo?.accountType === 'vendor') newSocket.on('vendor_notification', handleNewNotification);
        if (userInfo?.accountType === 'admin')  newSocket.on('admin_notification',  handleNewNotification);

        setSocket(newSocket);
        return () => { newSocket.disconnect(); };
    }, [userInfo, SOCKET_URL, fetchNotifications]);

    // ── NOTIFICATION ACTIONS ─────────────────────────────────────────────────
    const markAsRead = useCallback(async (id) => {
        if (!userInfo?.accountType) return;
        const role = userInfo.accountType === 'admin' ? 'admin' : 'vendor';
        try {
            const { default: NotificationService } = await import('api/NotificationService');
            await NotificationService.markAsRead(role, id);
            setNotifications((prev) => prev.map((n) => (n._id === id || n.id === id ? { ...n, read: true, status: 'read' } : n)));
        } catch (error) { console.error('Failed to mark as read:', error); }
    }, [userInfo]);

    const markAllAsRead = useCallback(async () => {
        if (!userInfo?.accountType) return;
        const role = userInfo.accountType === 'admin' ? 'admin' : 'vendor';
        try {
            const { default: NotificationService } = await import('api/NotificationService');
            await NotificationService.markAllAsRead(role);
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true, status: 'read' })));
        } catch (error) { console.error('Failed to mark all as read:', error); }
    }, [userInfo]);

    const deleteNotification = useCallback(async (id) => {
        if (!userInfo?.accountType) return;
        const role = userInfo.accountType === 'admin' ? 'admin' : 'vendor';
        try {
            const { default: NotificationService } = await import('api/NotificationService');
            await NotificationService.deleteNotification(role, id);
            setNotifications((prev) => prev.filter((n) => n._id !== id && n.id !== id));
        } catch (error) { console.error('Failed to delete notification:', error); }
    }, [userInfo]);

    const clearNotifications = useCallback(async () => {
        if (!userInfo?.accountType) return;
        const role = userInfo.accountType === 'admin' ? 'admin' : 'vendor';
        try {
            const { default: NotificationService } = await import('api/NotificationService');
            await NotificationService.deleteAllNotifications(role);
            setNotifications([]);
        } catch (error) { console.error('Failed to delete all notifications:', error); }
    }, [userInfo]);

    const value = {
        socket, notifications, isConnected,
        markAsRead, markAllAsRead, deleteNotification, clearNotifications, fetchNotifications
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
            {newOrderAlert && (
                <NewOrderAlertModal
                    alert={newOrderAlert}
                    onClose={() => setNewOrderAlert(null)}
                />
            )}
        </SocketContext.Provider>
    );
};
